/**
 * Client abonements («Абонемент», EN "Package") in the journal's payment
 * (boona: docs/codebase/abonements/README.md A3).
 *
 * A service line can be paid with one of the client's packages: the backend
 * takes a visit from it and the line owes no money. The choice travels with
 * `/admin-record/edit` as `paymentSection.abonementUses`; until the admin
 * changes something, the lines the backend already has paid by a package
 * (`GET /company/abonements/for-record/{id}`) are the state.
 */

export type AbonementUse = { service_id: number; abonement_id: number };

export type AbonementOption = {
  abonement_id: number;
  number: string;
  title: string;
  left: number | null;      // null = unlimited
  expires_at: string | null;
  value: number;            // per-visit value
};

export type RecordAbonements = {
  uses: (AbonementUse & { number: string; title: string; value: number })[];
  options: Record<string, AbonementOption[]>;
};

/** The uses the admin set in this modal, else what the backend has. */
export function currentUses(
  stateUses: AbonementUse[] | undefined | null,
  fetched: RecordAbonements | undefined | null,
): AbonementUse[] {
  if (Array.isArray(stateUses)) return stateUses;
  return (fetched?.uses ?? []).map((u) => ({ service_id: Number(u.service_id), abonement_id: Number(u.abonement_id) }));
}

export function coveredServiceIds(uses: AbonementUse[] | undefined | null): Set<number> {
  return new Set((uses ?? []).map((u) => Number(u.service_id)));
}

/** The lines that still owe money (a package pays the others). */
export function linesOwingMoney<T extends { id: number | string }>(services: T[], uses: AbonementUse[] | undefined | null): T[] {
  const covered = coveredServiceIds(uses);
  return (services ?? []).filter((s) => !covered.has(Number(s.id)));
}

/** Pay this line with `abonementId`, or with money again (null). */
export function setUse(uses: AbonementUse[], serviceId: number, abonementId: number | null): AbonementUse[] {
  const rest = (uses ?? []).filter((u) => Number(u.service_id) !== Number(serviceId));
  return abonementId ? [...rest, { service_id: Number(serviceId), abonement_id: Number(abonementId) }] : rest;
}

/** The packages that can pay this line — including the one already paying it. */
export function optionsForLine(
  fetched: RecordAbonements | undefined | null,
  serviceId: number,
  uses: AbonementUse[],
): AbonementOption[] {
  const opts = [...(fetched?.options?.[String(serviceId)] ?? [])];
  const current = uses.find((u) => Number(u.service_id) === Number(serviceId));
  if (current && !opts.some((o) => o.abonement_id === current.abonement_id)) {
    const known = fetched?.uses?.find((u) => u.abonement_id === current.abonement_id);
    opts.unshift({
      abonement_id: current.abonement_id, number: known?.number ?? "", title: known?.title ?? "",
      left: null, expires_at: null, value: known?.value ?? 0,
    });
  }
  return opts;
}

// ── selling from the journal (option a) ──────────────────────────────────────

export type PackageForSale = {
  id: number;
  title: string;
  price: number;
  pool: boolean;
  visits: number | null;
  period: number | null;
  period_unit: string | null;
  covers: number[];          // the appointment's services it covers
};

export type SaleResult = { abonement_id: number; number: string; title: string; lines: number[] };

/** After a sale: the new package pays the lines it can, unless a package already pays them. */
export function usesAfterSale(uses: AbonementUse[], sale: Pick<SaleResult, "abonement_id" | "lines">): AbonementUse[] {
  const taken = coveredServiceIds(uses);
  const added = (sale.lines ?? [])
    .filter((sid) => !taken.has(Number(sid)))
    .map((sid) => ({ service_id: Number(sid), abonement_id: Number(sale.abonement_id) }));
  return [...(uses ?? []), ...added];
}

type MethodLike = { account?: { id?: number | string; title?: string; isCash?: boolean; isDefault?: boolean } | null };

/** The payment section's accounts, for the Sell dialog; the default one first. */
export function saleAccounts(methods: MethodLike[] | undefined | null): { id: number; title: string }[] {
  const accounts = (methods ?? [])
    .map((m) => m?.account)
    .filter((a): a is NonNullable<MethodLike["account"]> => !!a && a.id != null)
    .map((a) => ({ id: Number(a.id), title: String(a.title ?? ""), isDefault: !!a.isDefault, isCash: !!a.isCash }));
  accounts.sort((x, y) => Number(y.isDefault) - Number(x.isDefault) || Number(y.isCash) - Number(x.isCash));
  return accounts.map(({ id, title }) => ({ id, title }));
}
